import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const fetchSheet = async (sheet) => {
  const response = await fetch(`/api/getSheetData?sheet=${encodeURIComponent(sheet)}`);
  return response.json();
};

const parseRows = (rows) => {
  if (!rows || !Array.isArray(rows)) return [];
  const headers = rows[0];
  return rows.slice(1).filter(row => row[0]).map(row => {
    const obj = {};
    headers.forEach((h, i) => { obj[h] = row[i]; });
    return obj;
  });
};

const consolidateMonthly = (fjcData, fjhData) => {
  const combined = {};
  [...fjcData, ...fjhData].forEach(row => {
    const key = `${row.Month}-${row.Year}`;
    if (!combined[key]) {
      combined[key] = { Month: row.Month, Year: row.Year, 'Portfolio Value': 0, 'Yield': 0, count: 0 };
    }
    combined[key]['Portfolio Value'] += (row['Portfolio Value'] || 0);
    combined[key]['Yield'] += (row['Yield'] || 0);
    combined[key].count += 1;
  });
  return Object.values(combined)
    .map(r => ({ ...r, Yield: r.count ? r['Yield'] / r.count : 0 }))
    .sort((a, b) => {
      if (String(a.Year) !== String(b.Year)) return String(a.Year).localeCompare(String(b.Year));
      return MONTHS.indexOf(a.Month) - MONTHS.indexOf(b.Month);
    });
};

// Reveals a section (fade + rise) the first time it scrolls into view.
const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.2 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
};

const CountUpStat = ({ value, prefix = '', suffix = '', decimals = 0, active }) => {
  const [display, setDisplay] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!active || value == null || startedRef.current) return;
    startedRef.current = true;
    const duration = 1400;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(value * eased);
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, value]);

  if (value == null) return <span>—</span>;

  const formatted = decimals > 0 ? display.toFixed(decimals) : Math.round(display).toLocaleString();
  return <span>{prefix}{formatted}{suffix}</span>;
};

const Sparkline = ({ data, width = 260, height = 56 }) => {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d['Portfolio Value']);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 8) - 4;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const lastX = width;
  const lastY = height - ((values[values.length - 1] - min) / range) * (height - 8) - 4;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block', overflow: 'visible' }}>
      <polyline points={points} fill="none" stroke="#b8c2cf" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
      <circle cx={lastX} cy={lastY} r="3" fill="#e4e8ee" />
    </svg>
  );
};

const WaveCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let t = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const drawWave = (offset, amplitude, wavelength, opacity, yBase) => {
      ctx.beginPath();
      ctx.moveTo(0, yBase);
      for (let x = 0; x <= canvas.width; x += 2) {
        const y = yBase
          + Math.sin((x / wavelength) + offset + t) * amplitude
          + Math.sin((x / (wavelength * 0.6)) + offset * 1.3 + t * 0.7) * (amplitude * 0.4);
        ctx.lineTo(x, y);
      }
      ctx.lineTo(canvas.width, canvas.height);
      ctx.lineTo(0, canvas.height);
      ctx.closePath();
      ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
      ctx.fill();
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const h = canvas.height;

      // Multiple wave layers from bottom up
      drawWave(0,    18, 320, 0.018, h * 0.55);
      drawWave(1.2,  14, 280, 0.022, h * 0.62);
      drawWave(2.4,  20, 360, 0.016, h * 0.68);
      drawWave(0.8,  12, 240, 0.025, h * 0.74);
      drawWave(3.1,  16, 300, 0.020, h * 0.80);
      drawWave(1.8,  10, 200, 0.028, h * 0.86);
      drawWave(0.4,  22, 400, 0.014, h * 0.45);
      drawWave(2.0,   8, 180, 0.030, h * 0.91);

      t += 0.004;
      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0, left: 0,
        width: '100%', height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
};

const Home = () => {
  const [series, setSeries] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [fjcRows, fjhRows] = await Promise.all([
          fetchSheet('FJC Dashboard'),
          fetchSheet('FJH Dashboard'),
        ]);
        const combined = consolidateMonthly(parseRows(fjcRows), parseRows(fjhRows));
        if (!cancelled) setSeries(combined);
      } catch (e) {
        // Fall back to static hero copy if live data can't be reached.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const latest = series[series.length - 1];
  const previous = series[series.length - 2];
  const trend = series.slice(-12);

  const delta = latest && previous ? latest['Portfolio Value'] - previous['Portfolio Value'] : null;
  const deltaPct = delta != null && previous['Portfolio Value'] ? (delta / previous['Portfolio Value']) * 100 : null;

  const [statsRef, statsVisible] = useReveal();
  const [cardsRef, cardsVisible] = useReveal();
  const [ctaRef, ctaVisible] = useReveal();

  const heroStats = [
    { label: 'Portfolio Value', kind: 'count', value: latest ? latest['Portfolio Value'] : null, prefix: '$', decimals: 0 },
    { label: 'Current Yield', kind: 'count', value: latest ? latest['Yield'] : null, suffix: '%', decimals: 2 },
    { label: 'Corporate Structure', kind: 'static', value: 'C-Corp' },
    { label: 'Washington, D.C.', kind: 'static', value: 'Est. 2025' },
  ];

  const infoCards = [
    {
      title: 'Our Strategy',
      text: 'We identify yield-producing exchange-traded funds and income-generating assets that offer attractive risk-adjusted returns. By leveraging our C-corp structure and banking relationships, we borrow against our holdings to acquire additional assets — compounding our yield over time.',
      link: '/about',
      linkText: 'Read More →',
    },
    {
      title: 'Our Structure',
      text: 'Flying Jib Capital Inc. serves as the management company and general partner, while Flying Jib Holdings LP holds the core asset portfolio. This two-entity architecture is designed for institutional credibility, tax efficiency, and long-term scalability.',
      link: '/founding',
      linkText: 'Our Story →',
    },
    {
      title: 'Transparency',
      text: 'We believe in full transparency with our banking partners and stakeholders. Our live portfolio and financial dashboard provide real-time insight into our holdings, performance metrics, and capital allocation — updated regularly from official account data.',
      link: '/dashboard',
      linkText: 'View Dashboard →',
    },
  ];

  return (
    <div style={styles.page}>
      <style>{HOME_STYLES}</style>

      {/* Hero Section */}
      <div style={styles.hero}>
        <WaveCanvas />
        <div style={styles.heroContent}>
          <p style={styles.eyebrow}>Washington, D.C.</p>
          <h1 style={styles.headline}>Yield-Focused<br />Asset Management</h1>
          <p style={styles.subheadline}>
            Flying Jib Capital is a C-corporation built to acquire and hold
            high-yield assets, leveraging disciplined capital allocation
            to generate consistent, compounding returns.
          </p>
          <div style={styles.buttonRow}>
            <Link to="/portfolio" style={styles.primaryButton} className="fj-hero-primary">View Portfolio</Link>
            <Link to="/about" style={styles.secondaryButton} className="fj-cta-secondary">Learn More</Link>
          </div>
        </div>

        {/* Sail mark watermark */}
        <div style={styles.watermark}>
          <svg width="320" height="320" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.04 }}>
            <polygon points="36,4 62,60 36,52" fill="white"/>
            <polygon points="36,16 52,60 36,52" fill="#0a1628"/>
            <polygon points="20,60 36,52 36,28" fill="white"/>
          </svg>
        </div>
      </div>

      {/* Stats Row */}
      <div ref={statsRef} style={styles.statsSection} className={`fj-reveal${statsVisible ? ' is-visible' : ''}`}>
        {heroStats.map((stat, i) => (
          <React.Fragment key={stat.label}>
            <div style={styles.statCard}>
              <span style={styles.statValue}>
                {stat.kind === 'count'
                  ? <CountUpStat value={stat.value} prefix={stat.prefix} suffix={stat.suffix} decimals={stat.decimals} active={statsVisible} />
                  : stat.value}
              </span>
              <span style={styles.statLabel}>{stat.label}</span>
            </div>
            {i < heroStats.length - 1 && <div style={styles.statDivider} />}
          </React.Fragment>
        ))}
      </div>

      {/* Three column info section */}
      <div style={styles.infoSection}>
        <div ref={cardsRef} style={styles.infoGrid}>
          {infoCards.map((card, i) => (
            <div
              key={card.title}
              style={{ ...styles.infoCard, transitionDelay: `${i * 120}ms` }}
              className={`fj-info-card fj-reveal${cardsVisible ? ' is-visible' : ''}`}
            >
              <div style={styles.infoAccent} className="fj-info-accent" />
              <h2 style={styles.infoTitle}>{card.title}</h2>
              <p style={styles.infoText}>{card.text}</p>
              <Link to={card.link} style={styles.infoLink}>{card.linkText}</Link>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div ref={ctaRef} style={styles.ctaSection} className={`fj-reveal${ctaVisible ? ' is-visible' : ''}`}>
        <div style={styles.ctaInner}>
          {trend.length > 1 && (
            <div style={styles.ctaSparkWrap}>
              <Sparkline data={trend} />
              {deltaPct != null && (
                <span style={{ ...styles.ctaDelta, color: deltaPct >= 0 ? '#7fd4ac' : '#e29a92' }}>
                  {deltaPct >= 0 ? '▲' : '▼'} {Math.abs(deltaPct).toFixed(1)}% MoM
                </span>
              )}
            </div>
          )}
          <h2 style={styles.ctaTitle}>Explore Our Portfolio</h2>
          <p style={styles.ctaText}>
            View our current holdings, performance data, and financial metrics
            in real time through our live portfolio and dashboard.
          </p>
          <div style={styles.buttonRow}>
            <Link to="/portfolio" style={styles.ctaPrimary} className="fj-hero-primary">Portfolio</Link>
            <Link to="/dashboard" style={styles.ctaSecondary} className="fj-cta-secondary">Dashboard</Link>
          </div>
        </div>
      </div>

    </div>
  );
};

const HOME_STYLES = `
  .fj-reveal {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .fj-reveal.is-visible {
    opacity: 1;
    transform: translateY(0);
  }
  .fj-info-card {
    transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease;
  }
  .fj-info-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 24px 48px rgba(10, 22, 40, 0.14);
  }
  .fj-info-accent {
    transition: width 0.4s ease, background 0.4s ease;
  }
  .fj-info-card:hover .fj-info-accent {
    width: 64px;
    background: linear-gradient(90deg, #8a94a3, #e4e8ee 55%, #8a94a3);
  }
  .fj-cta-secondary {
    transition: background-color 0.3s ease, border-color 0.3s ease;
  }
  .fj-cta-secondary:hover {
    background-color: rgba(184, 194, 207, 0.1);
    border-color: #b8c2cf;
  }
  .fj-hero-primary {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  .fj-hero-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0,0,0,0.18);
  }
`;

const styles = {
  page: {
    backgroundColor: '#f4f6f9',
    minHeight: '100vh',
  },
  hero: {
    position: 'relative',
    backgroundColor: '#0a1628',
    minHeight: '85vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    padding: '80px 40px',
  },
  heroContent: {
    position: 'relative',
    zIndex: 2,
    maxWidth: '760px',
    textAlign: 'center',
  },
  eyebrow: {
    fontSize: '11px',
    letterSpacing: '4px',
    textTransform: 'uppercase',
    color: 'rgba(168, 184, 204, 0.7)',
    fontFamily: 'Georgia, serif',
    marginBottom: '24px',
  },
  headline: {
    fontSize: '64px',
    fontWeight: '700',
    color: '#ffffff',
    fontFamily: 'Georgia, serif',
    letterSpacing: '1px',
    lineHeight: '1.1',
    marginBottom: '28px',
  },
  subheadline: {
    fontSize: '18px',
    color: 'rgba(168, 184, 204, 0.85)',
    fontFamily: 'Georgia, serif',
    lineHeight: '1.9',
    marginBottom: '44px',
    maxWidth: '580px',
    margin: '0 auto 44px auto',
  },
  buttonRow: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  primaryButton: {
    backgroundColor: '#ffffff',
    color: '#0a1628',
    padding: '15px 36px',
    textDecoration: 'none',
    fontFamily: 'Georgia, serif',
    fontSize: '13px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    fontWeight: '700',
    display: 'inline-block',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    color: '#ffffff',
    padding: '15px 36px',
    textDecoration: 'none',
    fontFamily: 'Georgia, serif',
    fontSize: '13px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    border: '1px solid rgba(255,255,255,0.3)',
    display: 'inline-block',
  },
  watermark: {
    position: 'absolute',
    right: '-40px',
    bottom: '-40px',
    zIndex: 1,
    pointerEvents: 'none',
  },
  statsSection: {
    backgroundColor: '#ffffff',
    padding: '40px 60px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderBottom: '1px solid #e0e8f0',
    flexWrap: 'wrap',
  },
  statCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    padding: '0 48px',
  },
  statValue: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#0a1628',
    fontFamily: 'Georgia, serif',
    letterSpacing: '0.5px',
  },
  statLabel: {
    fontSize: '11px',
    color: '#7a8fa6',
    fontFamily: 'Georgia, serif',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  statDivider: {
    width: '1px',
    height: '40px',
    backgroundColor: '#e0e8f0',
  },
  infoSection: {
    backgroundImage: `
      linear-gradient(rgba(10,22,40,0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(10,22,40,0.035) 1px, transparent 1px)
    `,
    backgroundSize: '48px 48px',
    backgroundColor: '#f4f6f9',
    padding: '80px 40px',
  },
  infoGrid: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '32px',
  },
  infoCard: {
    backgroundColor: '#ffffff',
    padding: '40px 32px',
    boxShadow: '0 1px 3px rgba(10, 22, 40, 0.06)',
  },
  infoAccent: {
    width: '40px',
    height: '3px',
    backgroundColor: '#b8c2cf',
    marginBottom: '24px',
  },
  infoTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#0a1628',
    fontFamily: 'Georgia, serif',
    marginBottom: '16px',
    letterSpacing: '0.5px',
  },
  infoText: {
    fontSize: '15px',
    color: '#4a6080',
    fontFamily: 'Georgia, serif',
    lineHeight: '1.9',
    marginBottom: '24px',
  },
  infoLink: {
    fontSize: '13px',
    color: '#0a1628',
    fontFamily: 'Georgia, serif',
    letterSpacing: '1px',
    textDecoration: 'none',
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  ctaSection: {
    backgroundColor: '#0a1628',
    padding: '80px 40px',
  },
  ctaInner: {
    maxWidth: '600px',
    margin: '0 auto',
    textAlign: 'center',
  },
  ctaSparkWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '32px',
  },
  ctaDelta: {
    fontSize: '12px',
    fontFamily: 'Georgia, serif',
    letterSpacing: '1px',
    fontWeight: '700',
  },
  ctaTitle: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#ffffff',
    fontFamily: 'Georgia, serif',
    letterSpacing: '1px',
    marginBottom: '16px',
  },
  ctaText: {
    fontSize: '16px',
    color: 'rgba(168, 184, 204, 0.85)',
    fontFamily: 'Georgia, serif',
    lineHeight: '1.9',
    marginBottom: '36px',
  },
  ctaPrimary: {
    backgroundColor: '#ffffff',
    color: '#0a1628',
    padding: '14px 32px',
    textDecoration: 'none',
    fontFamily: 'Georgia, serif',
    fontSize: '13px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    fontWeight: '700',
    display: 'inline-block',
  },
  ctaSecondary: {
    backgroundColor: 'transparent',
    color: '#ffffff',
    padding: '14px 32px',
    textDecoration: 'none',
    fontFamily: 'Georgia, serif',
    fontSize: '13px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    border: '1px solid rgba(255,255,255,0.3)',
    display: 'inline-block',
  },
};

export default Home;
